import {
  all,
  take,
  takeEvery,
  put,
  select,
  call,
  fork,
  takeLatest,
  race,
  cancel,
} from 'redux-saga/effects'
import { delay } from 'redux-saga'
import { isNull, isEmpty } from 'lodash-es'
import moment from 'moment'
import URI from 'urijs'
import { intl } from 'lang/intl'

import { intToBool, waitFor } from '../sharedLegacy/utils'
import { setPlaybackState } from '../firebase/hooks/useVideoPlayback'
import { showNotice } from '../Layout/Notices/Notices'
import { getSiteData } from '../global/blocs/useSite'
import { logError } from '../Logging/log'

import { actionNames } from './actions'
import { actionNames as playbackActionNames } from './shared/actions.playback'
import {
  getWebinarInfo,
  getLiveData,
  toggleLive,
  getSecondCamCode,
  fetchLiveFunnelData,
  fetchLiveMetrics,
} from './api'
import {
  isBroadcastStreaming,
  isBroadcastLive,
  isBroadcastRecording,
} from './selectors'

let hideConnectingLoader = null
let hideLiveTogglingLoader
let fetchInfoInterval = 2000

const getRole = (state) => state.webinar.role
const getAuthInfo = (state) => state.webinar.authInfo
const getLiveId = (state) => state.webinar.liveId
const getPresenterToken = (state) => state.webinar.presenterToken
const getStreamingStartTriggerTime = (state) =>
  state.webinar.streamingStartTriggeredAt
const getLiveTogglingTriggeredAt = (state) =>
  state.webinar.liveTogglingTriggeredAt
const getRecordingToggleTriggeredAt = (state) =>
  state.webinar.recordingToggleTriggeredAt
const getPlaybackState = (state) => state.webinarPlayback.playbackState
const getLiveToken = (state) => state.webinar.liveAuthToken
const hasGearMode = (state) => state.webinar.isGearMode
const getEndingWebinarState = (state) => state.webinar.isEndingWebinar
const getWebinarEndedState = (state) => state.webinar.webinarEnded
const hasEndedWebinar = (state) => state.webinar.webinarEnded
const getWebinarInfoState = (state) => state.webinar.webinarInfo
const getReconnectingState = (state) => state.webinar.isReconnecting
const getConnectingModalState = (state) =>
  state.webinar.isConnectingModalVisible
const getTwilioConnectedState = (state) => state.webinar.twilioConnected
const getCriticalError = (state) => state.webinar.criticalError
const getSecondCamState = (state) => state.webinar.isSecondCam
const getFacingMode = (state) => state.webinar.facingMode
const rotateWaitingText = (state) => state.webinar.shouldRotateWaitingText
const getQuestionOverlayCommentId = (state) =>
  state.webinar.questionOverlayCommentId

let isRedirecting = false
const RECONNECT_TIMEOUT_SEC = 120
let pollOverlayRealtimeTask

const hideLoader = () => {
  if (!isNull(hideConnectingLoader)) {
    hideConnectingLoader()
    hideConnectingLoader = null
  }
}

function* watchSimulateHeadlessDisconnect() {
  yield takeEvery(actionNames.SIMULATE_HEADLESS_DISCONNECT, () => {})
}

function* raiseNotification(type, message) {
  const isSecondCam = yield select(getSecondCamState)
  const hasCriticalError = yield select(getCriticalError)
  const isLoadingScreenShown = yield select(getConnectingModalState)

  if (isSecondCam || hasCriticalError || isLoadingScreenShown) {
    return
  }

  showNotice({
    message,
    expirationMillis: 5e3,
    isClosable: true,
  })
}

function* watchHeadlessLeaving() {
  while (true) {
    yield take(actionNames.HEADLESS_LEFT)

    const { leftForGood } = yield race({
      leftForGood: delay(20000),
      rejoined: take(actionNames.HEADLESS_REJOINED),
    })

    const isStreamingToLive = yield select(isBroadcastStreaming)
    const isEndingWebinar = yield select(getEndingWebinarState)
    const webinarEnded = yield select(hasEndedWebinar)

    if (leftForGood && isStreamingToLive && !isEndingWebinar && !webinarEnded) {
      yield call(
        raiseNotification,
        'error',
        'We lost connection to your audience. Please contact our support that will help you to restore streaming.'
      )
    }
  }
}

function* stopVideoPlayback() {
  const playbackState = yield select(getPlaybackState)

  if (!isNull(playbackState)) {
    setPlaybackState('stop_intent_sent')

    yield put({ type: playbackActionNames.VIDEO_PLAYBACK_STOPPED })
  }
}

function* reconnectStreaming(hasExpired) {
  const isReconnecting = yield select(getReconnectingState)
  const isConnectingModalVisible = yield select(getConnectingModalState)
  const hasCriticalError = yield select(getCriticalError)

  if (hasCriticalError) {
    yield
    return
  }

  if (isReconnecting && !hasExpired) {
    yield
    return
  }
  if (isReconnecting) {
    yield put({
      type: actionNames.CRITICAL_ERROR_OCCURED,
      error: 'RECONNECT_ERROR',
    })
    return
  }

  hideLoader()

  yield put({ type: actionNames.RECONNECT_STREAMING })

  if (!isConnectingModalVisible) {
    hideConnectingLoader = showNotice({
      message: intl.formatMessage({
        defaultMessage: 'Reconnecting...',
        id: 'dOGweU',
      }),
    })
  }

  fetchInfoInterval = 2000
}

function* showLiveStatusNotifications(info) {
  const liveTogglingTriggeredAt = yield select(getLiveTogglingTriggeredAt)
  const isEndingWebinar = yield select(getEndingWebinarState)

  if (
    info.broadcasting_p === 0 &&
    isNull(liveTogglingTriggeredAt) &&
    !isEndingWebinar
  ) {
    yield call(raiseNotification, 'error', 'Live stopped')
  }
}

// TODO (noahlaux): This collides with error state not re-throwing
// eslint-disable-next-line consistent-return
function* fetchLiveDataWorker(liveId, initialLoad) {
  const liveAuthToken = yield select(getLiveToken)

  try {
    const isGearMode = yield select(hasGearMode)
    const loadGearModeData = initialLoad && isGearMode
    const data = yield call(getLiveData, liveId, liveAuthToken, {
      includePublishDetails: loadGearModeData,
      includeFmleURL: loadGearModeData,
    })

    yield put({ type: actionNames.FETCH_LIVE_DATA_SUCCESS, data })
    return data
  } catch (err) {
    yield put({
      type: actionNames.CRITICAL_ERROR_OCCURED,
      error: 'LIVE_DATA_LOAD_ERROR',
    })
  }
}

// here we end webinar for presenters
// limitCallCount - mainly used in test so it waits less than 10 seconds
// TODO (noahlaux): This collides with error state not re-throwing
// eslint-disable-next-line consistent-return
function* endPresenterWebinar(info, limitCallCount = 10) {
  const liveId = yield select(getLiveId)
  let liveData
  let count = 0

  do {
    count += 1
    // if the loop is not done in 10 requests (around 10-15s) stop the execution
    // reconnect may cause the broadcasting_p to be 0 but the webinar is not yet over
    if (count > limitCallCount) {
      return undefined
    }

    yield delay(1000)
    liveData = yield call(fetchLiveDataWorker, liveId)
  } while (liveData.live_status !== 'previous')

  // this is the end - WEBINAR_ENDED will cancel all sagas originated form the webinarInfo loop
  // the tow actions below should be send at the same time
  yield put({ type: actionNames.WEBINAR_INFO_FETCH_SUCCESS, data: info })
  yield put({ type: actionNames.WEBINAR_ENDED })
}

function* handlePresenterLiveStatus(info) {
  const isBroadcastingLive = yield select(isBroadcastLive)

  if (intToBool(info.broadcasting_p) !== isBroadcastingLive) {
    if (info.broadcasting_p === 1) {
      yield fork(showLiveStatusNotifications, info)
    }

    if (info.broadcasting_p === 0) {
      yield call(endPresenterWebinar, info)
    }
  }
}

function* reconnectBroadcast() {
  yield put({
    type: actionNames.LIVE_TOGGLE,
    isActive: true,
    momentDate: moment(),
    isReconnect: true,
  })
}

function* fetchWebinarOverMetricsWorker(liveId) {
  // The webinar room is rendered in an iframe, which may have different url than the site url
  // We should make sure that we run the requests below from the site url due to cookies
  // this should be removed once we remove the iframe for the webinar room, which
  // will happen when Chrome removes the need of an extension to share screen ... coming soon
  const { https_url: domain } = yield call(getSiteData)

  try {
    // extract data from body since we use jsonp for now
    const [
      {
        body: { funnel },
      },
      {
        body: { metrics },
      },
    ] = yield all([
      call(fetchLiveFunnelData, liveId, domain),
      call(fetchLiveMetrics, liveId, domain),
    ])

    yield put({
      type: actionNames.FETCH_WEBINAR_OVER_METRICS_SUCCESS,
      funnel,
      metrics,
    })
  } catch (err) {
    yield put({ type: actionNames.FETCH_WEBINAR_OVER_METRICS_ERROR })
  }
}

function* endingWebinarWorker() {
  // first fetch metrics and after that end webinar for producer
  const liveId = yield select(getLiveId)
  yield call(fetchWebinarOverMetricsWorker, liveId)

  yield put({ type: actionNames.WEBINAR_ENDED })
}

function* handleProducerLiveStatus(info) {
  const liveTogglingTriggeredAt = yield select(getLiveTogglingTriggeredAt)
  const isBroadcastingLive = yield select(isBroadcastLive)
  const isEndingWebinar = yield select(getEndingWebinarState)
  const isLive = intToBool(info.broadcasting_p)

  // have not started to live broadcast in 30 seconds
  if (
    !isNull(liveTogglingTriggeredAt) &&
    moment().diff(liveTogglingTriggeredAt, 'seconds') > 30
  ) {
    if (hideLiveTogglingLoader) {
      hideLiveTogglingLoader()
    }
    fetchInfoInterval = 15000
    yield call(raiseNotification, 'error', 'Failed to toggle live')
    yield put({ type: actionNames.LIVE_TOGGLE_FAILURE })
    yield fork(reconnectBroadcast)
  } else if (isLive !== isBroadcastingLive) {
    if (hideLiveTogglingLoader) {
      hideLiveTogglingLoader()
      fetchInfoInterval = 15000
    }

    yield fork(showLiveStatusNotifications, info)

    if (!isLive && isEndingWebinar) {
      yield call(endingWebinarWorker)
    }

    yield put({ type: actionNames.LIVE_TOGGLE_SUCCESS, isLive })
  }
}

function* handleStreamingStatus(info) {
  const streamingStartTriggeredAt = yield select(getStreamingStartTriggerTime)
  const isStreamingToLive = yield select(isBroadcastStreaming)
  const isEndingWebinar = yield select(getEndingWebinarState)
  const webinarEnded = yield select(getWebinarEndedState)
  const isGearMode = yield select(hasGearMode)

  if (isStreamingToLive) {
    const isReconnecting = yield select(getReconnectingState)

    // stopped streaming
    if (info.streaming_p === 0 && !isEndingWebinar) {
      yield fork(stopVideoPlayback)

      yield call(
        raiseNotification,
        'warning',
        isGearMode ? 'Streaming stopped' : 'Lost connection.'
      )
      yield put({ type: actionNames.BROADCAST_STREAMING_STOPPED })

      if (!isGearMode && !webinarEnded) {
        yield fork(reconnectStreaming)
      }
    } else if (isReconnecting) {
      hideLoader()
      yield put({ type: actionNames.RECONNECT_STREAMING_SUCCESS })
    }
  } else if (info.streaming_p === 1) {
    // just started to stream
    hideLoader()
    fetchInfoInterval = 15000
    yield put({ type: actionNames.BROADCAST_STREAMING_STARTED })
  } else if (
    moment().diff(streamingStartTriggeredAt, 'seconds') > RECONNECT_TIMEOUT_SEC
  ) {
    // have not started to stream in 45 seconds

    hideLoader()
    fetchInfoInterval = 15000

    yield put({ type: actionNames.BROADCAST_STREAMING_START_FAILED })
    yield fork(reconnectStreaming, true)
  }
}

function* handleRecordingStatus(info) {
  const isBroadcastingRecording = yield select(isBroadcastRecording)
  const recordingToggleTriggeredAt = yield select(getRecordingToggleTriggeredAt)
  const isEndingWebinar = yield select(getEndingWebinarState)
  const webinarEnded = yield select(hasEndedWebinar)

  if (intToBool(info.recording_p) !== isBroadcastingRecording) {
    if (info.recording_p === 0 && !isEndingWebinar && !webinarEnded) {
      yield call(raiseNotification, 'error', 'Recording stopped')
    } else if (info.recording_p === 1) {
      yield put({ type: actionNames.RECORDING_STARTED })
    }
    return
  }

  if (
    !isNull(recordingToggleTriggeredAt) &&
    moment().diff(recordingToggleTriggeredAt, 'seconds') > 30
  ) {
    if (info.recording_p === 0) {
      yield call(raiseNotification, 'error', 'Recording has not started')
      yield put({ type: actionNames.RECORDING_FAILED })
    }
  }
}

function* fetchWebinarInfo() {
  const liveId = yield select(getLiveId)
  const role = yield select(getRole)
  const presenterToken = yield select(getPresenterToken)

  try {
    let info

    if (role === 'producer') {
      info = yield call(getWebinarInfo, liveId)

      yield fork(handleProducerLiveStatus, info)
      yield fork(handleStreamingStatus, info)
      yield fork(handleRecordingStatus, info)
      yield put({ type: actionNames.WEBINAR_INFO_FETCH_SUCCESS, data: info })
    } else {
      info = yield call(getWebinarInfo, liveId, presenterToken)

      yield call(handlePresenterLiveStatus, info)
      yield put({ type: actionNames.WEBINAR_INFO_FETCH_SUCCESS, data: info })
    }
  } catch (err) {
    if (isRedirecting) {
      return
    }

    yield put({ type: actionNames.WEBINAR_INFO_FETCH_FAILED })
  }
}

function* cancelPollRealtimeUpdatesOverlayTask() {
  if (pollOverlayRealtimeTask) {
    yield cancel(pollOverlayRealtimeTask)
    pollOverlayRealtimeTask = null
  }
}

function* setSpeakerOverlay() {
  yield call(cancelPollRealtimeUpdatesOverlayTask)
}

function* toggleQuestionOverlay(action) {
  yield call(cancelPollRealtimeUpdatesOverlayTask)

  try {
    const currentOverlayCommentId = yield select(getQuestionOverlayCommentId)

    if (currentOverlayCommentId !== action.question.comment_id) {
      yield put({
        type: actionNames.SHOW_QUESTION_OVERLAY,
        questionOverlayCommentId: action.question.comment_id,
      })
    }
  } catch (err) {
    logError(err)
  }
}

function* showCornerTagOverlay() {
  yield call(cancelPollRealtimeUpdatesOverlayTask)

  try {
    yield put({ type: actionNames.CORNER_TAG_OVERLAY_TOGGLED, isShowing: true })
  } catch (err) {
    logError(err)
    yield call(raiseNotification, 'error', 'Could not publish overlay')
  }
}

function* hideCornerTagOverlay() {
  try {
    yield put({
      type: actionNames.CORNER_TAG_OVERLAY_TOGGLED,
      isShowing: false,
    })
  } catch (err) {
    yield call(raiseNotification, 'error', 'Could not clear overlay')
  }
}

function* watchSpeakerOverlay() {
  const TIME_TILL_HIDE = 6000
  const REST_TIME_TO_BLOCK = 9000

  while (true) {
    const action = yield take(actionNames.SHOW_SPEAKER_OVERLAY)
    yield fork(setSpeakerOverlay, action)

    const { timer, hide } = yield race({
      timer: delay(TIME_TILL_HIDE),
      question: take(actionNames.TOGGLE_QUESTION_OVERLAY),
      cornerTag: take(actionNames.SHOW_CORNER_TAG_OVERLAY),
      poll: take(actionNames.TOGGLE_POLL_OVERLAY),
      hide: take(actionNames.HIDE_SPEAKER_OVERLAY),
    })

    if (timer || hide) {
      // block for 9 seconds so we have a total of 15 seconds between
      // switching speakers
      yield race({
        timerTwo: delay(REST_TIME_TO_BLOCK),
        question: take(actionNames.TOGGLE_QUESTION_OVERLAY),
        cornerTag: take(actionNames.SHOW_CORNER_TAG_OVERLAY),
        poll: take(actionNames.TOGGLE_POLL_OVERLAY),
      })
    }
  }
}

function* watchOverlays() {
  yield fork(watchSpeakerOverlay)
  yield takeLatest(actionNames.TOGGLE_QUESTION_OVERLAY, toggleQuestionOverlay)
  yield takeLatest(actionNames.SHOW_CORNER_TAG_OVERLAY, showCornerTagOverlay)
  yield takeLatest(actionNames.HIDE_CORNER_TAG_OVERLAY, hideCornerTagOverlay)
}

function* infiniteFetchWebinarInfoLoop() {
  /*
    We poll for webinar info to detect if Wowza went live
    live = started broadcasting and started streaming
    so checking for `broadcasting_p` and `streaming_p`
    in regular intervals
  */

  while (true) {
    yield fork(fetchWebinarInfo)
    yield delay(fetchInfoInterval)
  }
}

function* watchWebinarInfo() {
  yield take(actionNames.CONNECT_TO_WEBINAR)
  const fetchWebinarInfoLoop = yield fork(infiniteFetchWebinarInfoLoop)

  yield take(actionNames.WEBINAR_ENDED)
  yield cancel(fetchWebinarInfoLoop)
}

function* handleLiveSwitchState(action) {
  const liveId = yield select(getLiveId)

  if (action.isReconnect) {
    hideLiveTogglingLoader = showNotice({
      message: intl.formatMessage({
        defaultMessage: 'Starting live webinar...',
        id: 'AOEmCS',
      }),
    })
  }

  try {
    yield call(toggleLive, liveId, action.isActive)

    if (action.isActive) {
      yield fork(fetchLiveDataWorker, liveId)
      fetchInfoInterval = 2000
      yield fork(fetchWebinarInfo)
    }
  } catch (err) {
    logError(err)

    if (hideLiveTogglingLoader) {
      hideLiveTogglingLoader()
    }

    yield put({ type: actionNames.LIVE_TOGGLE_FAILURE })

    const errorText = action.isActive
      ? 'Failed to go live'
      : 'Failed to end livestream'

    yield call(raiseNotification, 'error', errorText)

    if (action.isActive) {
      yield fork(reconnectBroadcast)
    }
  }
}

function* toggleFacingModeWorker() {
  const facingMode = yield select(getFacingMode)
  const newFacingMode = facingMode === 'user' ? 'environment' : 'user'

  yield put({
    type: actionNames.TOGGLE_FACING_MODE_SUCCESS,
    facingMode: newFacingMode,
  })
}

function* secondCamTriggeredWorker() {
  const liveId = yield select(getLiveId)
  const presenterToken = yield select(getPresenterToken)

  try {
    const { webinar } = yield call(getSecondCamCode, liveId, presenterToken)

    yield put({
      type: actionNames.SECOND_CAMERA_FETCH_CODE_SUCCESS,
      token: webinar.camera_authentication,
    })
  } catch (err) {
    yield call(
      raiseNotification,
      'error',
      'Could not generate code for activating second cam'
    )
    yield put({ type: actionNames.SECOND_CAMERA_FETCH_CODE_ERROR })
  }
}

function* watchSecondCam() {
  yield takeEvery(actionNames.SECOND_CAMERA_TRIGGERED, secondCamTriggeredWorker)
}

function* watchLiveCountdown() {
  yield take(actionNames.LIVE_COUNTDOWN_ZERO)

  if (yield select(isBroadcastLive)) {
    return
  }

  const role = yield select(getRole)
  let message

  if (role === 'producer') {
    message = intl.formatMessage({
      defaultMessage: 'The webinar is scheduled to start now. Go live shortly.',
      id: 'Mi089d',
    })
  } else {
    message = intl.formatMessage({
      defaultMessage:
        'The webinar is scheduled to start now. Producer will start streaming shortly.',
      id: 'ipSWvj',
    })
  }

  yield call(raiseNotification, 'warning', message)
}

function* watchFacingModeToggle() {
  yield takeEvery(actionNames.TOGGLE_FACING_MODE, toggleFacingModeWorker)
}

function* watchLiveToggle() {
  yield takeLatest(actionNames.LIVE_TOGGLE, function* onLiveToggle(action) {
    if (!action.isActive) {
      yield put({ type: actionNames.ENDING_WEBINAR })
    }

    yield fork(handleLiveSwitchState, action)
  })
}

function* handleHTTPSredirect() {
  const uri = URI(window.location.href)

  if (uri.protocol() === 'http' && uri.hostname() !== 'localhost') {
    isRedirecting = true
    uri.protocol('https')
    window.location.href = uri.toString()
  }
  yield
}

function* handleBackgroundFading(role) {
  const hasWebinarInfoLoaded = !isEmpty(yield select(getWebinarInfoState))
  const isBroadcastingStreaming = yield select(isBroadcastStreaming)
  const twilioConnected = yield select(getTwilioConnectedState)
  const waitForWebinarInfo = [
    hasWebinarInfoLoaded,
    actionNames.WEBINAR_INFO_FETCH_SUCCESS,
    actionNames.WEBINAR_INFO_FETCH_FAILED,
  ]
  const waitForStreaming = [
    isBroadcastingStreaming,
    actionNames.BROADCAST_STREAMING_STARTED,
  ]
  const waitForTwilio = [twilioConnected, actionNames.TWILIO_CONNECTED]

  if (role === 'producer') {
    const waitList = [waitForWebinarInfo]

    if (!(yield select(hasGearMode))) {
      waitList.push(waitForStreaming)
      waitList.push(
        waitForTwilio
      ) /* this is needed for cases where streaming_p is 1 because headless has been running */
    }

    yield call(waitFor, waitList)
  } else {
    const waitList = [waitForWebinarInfo, waitForTwilio]

    yield call(waitFor, waitList)
  }

  // give loaders some time to disappear
  yield delay(1000)
  yield put({ type: actionNames.HIDE_CONNECTING_MODAL })
}

function* fetchInitialData(liveId) {
  const liveAuthToken = yield select(getLiveToken)
  if (isNull(liveAuthToken)) {
    yield take(actionNames.FETCH_CONNECTION_INFO_SUCCESS)
  }
  yield fork(fetchLiveDataWorker, liveId, true)
  yield fork(fetchWebinarInfo)
}

function* watchRedirectToManagePage(liveId) {
  yield take(actionNames.REDIRECT_TO_MANAGE_PAGE)
  const siteData = yield call(getSiteData)
  window.location.href = `${siteData.https_url}/manage/webinar/${liveId}`
}

function* watchErrors() {
  yield takeEvery(
    actionNames.RAISE_NOTIFICATION,
    function* errorHandler(action) {
      yield fork(raiseNotification, action.notificationType, action.message)
    }
  )
}
function* alternateWaitingText() {
  let shouldRotateWaitingText = yield select(rotateWaitingText)
  const messages = [
    intl.formatMessage({
      defaultMessage: 'Get ready, look sharp.',
      id: '3QAMcj',
    }),
    intl.formatMessage({
      defaultMessage: 'We are checking your network settings.',
      id: '4PIpUF',
    }),
    intl.formatMessage({
      defaultMessage:
        'We are connecting you to the room and starting up something magical in the clouds.',
      id: 'Ab0dLW',
    }),
    intl.formatMessage({
      defaultMessage: 'Make sure you are on a stable connection.',
      id: 'Vr1iaN',
    }),
    intl.formatMessage({
      defaultMessage: 'Make sure to use powerful hardware.',
      id: 'IkUoOT',
    }),
    intl.formatMessage({
      defaultMessage: 'Make sure that you put power on your laptop.',
      id: 'nVFShZ',
    }),
    intl.formatMessage({
      defaultMessage:
        'Make sure to shut down unnecessary apps and notifications.',
      id: 'VsuKak',
    }),
  ]
  let counter = 0

  while (shouldRotateWaitingText) {
    yield put({
      type: actionNames.UPDATE_CONNECTING_MODAL_TEXT,
      text: messages[counter],
    })

    counter += 1

    if (counter === messages.length) {
      counter = 0
    }

    shouldRotateWaitingText = yield select(rotateWaitingText)

    yield delay(4000)
  }
}

function* loadPlaybackVideos(liveId) {
  yield call(waitFor, [
    [
      !isEmpty(yield select(getAuthInfo)),
      actionNames.FETCH_CONNECTION_INFO_SUCCESS,
    ],
  ])

  if (yield select(hasGearMode)) {
    return
  }

  yield put({ type: playbackActionNames.LOAD_PLAYBACK_VIDEOS, liveId })
}

function* watchInit() {
  const action = yield take(actionNames.INIT_WEBINAR)
  const { liveId, role } = action.options
  const isProducer = role === 'producer'

  // redirect to https if we are currently on http
  yield call(handleHTTPSredirect)

  // start showing different messages while loading
  yield fork(alternateWaitingText)

  if (isProducer) {
    yield fork(watchRedirectToManagePage, liveId)
  }

  // we make sure we have connect info in the call below so it blocks saga until we have it
  yield call(fetchInitialData, liveId)
  yield fork(handleBackgroundFading, role)

  const isInGearMode = yield select(hasGearMode)

  if (isInGearMode && isProducer) {
    yield put({ type: actionNames.CONNECT_TO_WEBINAR })
  } else if (!isInGearMode && isProducer) {
    yield fork(loadPlaybackVideos, liveId)
  } else if (isInGearMode && !isProducer) {
    yield put({
      type: actionNames.CRITICAL_ERROR_OCCURED,
      error: 'GEAR_MODE_REMOTE_SPEAKERS_UNSUPPORTED',
    })
  }
}

export default [
  watchInit,
  watchWebinarInfo,
  watchLiveToggle,
  watchLiveCountdown,
  watchFacingModeToggle,
  watchSecondCam,
  watchErrors,
  watchOverlays,
  watchSimulateHeadlessDisconnect,
  watchHeadlessLeaving,
]

export {
  watchWebinarInfo,
  watchInit,
  watchRedirectToManagePage,
  fetchLiveDataWorker,
  endingWebinarWorker,
  endPresenterWebinar,
  handlePresenterLiveStatus,
}
